/** Size an image is scaled to so that its longest edge is at most `maxEdge`; never upscales. */
export function fitWithin(width: number, height: number, maxEdge: number): { width: number; height: number } {
  if (width <= 0 || height <= 0) throw new RangeError('image size must be positive');
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** Approximate decoded size of a base64 data URL in bytes. */
export function dataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  const payload = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  return Math.floor((payload.length * 3) / 4) - padding;
}

/** Spec 8: the main image is re-encoded at this JPEG quality (ca. 300–600 KB at 2048 px). */
export const PHOTO_JPEG_QUALITY = 0.82;
/** The 400 px thumbnail; the server refuses one above 200 KB, this stays far below. */
export const THUMB_JPEG_QUALITY = 0.78;

/**
 * Both target sizes of a photo (spec 8), the same on every client: the main
 * image's longest edge at most 2048 px, the thumbnail's at most 400 px, never
 * upscaled. The web scales through a canvas, the native app through
 * expo-image-manipulator; both ask this function.
 */
export function photoSizes(
  width: number,
  height: number,
  limits: { main: number; thumb: number },
): { main: { width: number; height: number }; thumb: { width: number; height: number } } {
  return { main: fitWithin(width, height, limits.main), thumb: fitWithin(width, height, limits.thumb) };
}
